import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { useHasPermission, useQuery } from "../../common/Hooks";
import Sync from "../../services/sync";
import Utils from "../../utils/utils";
import Toolbar from "../controls/Toolbar";
import LogsDownload from "./SyncLogsDownload";

const SessionList = () => {
    const [translate] = useTranslation();
    const { id: deploymentId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const queryParams = useQuery();

    const [sessionParams, setSessionParams] = useState({});
    const [sessionData, setSessionData] = useState([]);
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);

    const permission = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "sessionLogs"
    });

    const [showDownloadLogsModal, setShowDownloadLogsModal] = useState(false);

    useEffect(() => {
        setSessionParams({
            pageIncrement: 0,
            subTypeId: deploymentId
        });
        setCurrentPage(1);
        setHasNextPage(true);
    }, [deploymentId]);

    const sessionId = useMemo(() => {
        return queryParams.get("sessionId");
    }, [queryParams]);

    const getSessions = useCallback(async (params) => {
        setSessionParams({});
        const response = await Sync.getAllSyncSessionsInDeployment(
            params.subTypeId,
            params.firstCursor,
            params.lastCursor,
            params.pageIncrement < 0
        );
        if (!response.error) {
            setSessionData(response.data);
            setCurrentPage(page => {
                const result = page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });

            if (response.data.length > 0 && !sessionId) {
                history.push("?sessionId=" + response.data[0].sessionId)
            }
        }
    }, [sessionId, history]);

    useEffect(() => {
        if (sessionParams.subTypeId) {
            getSessions(sessionParams);
        }
    }, [permission, sessionParams, getSessions]);

    const removeSessionIdFromURL = useCallback(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.has("sessionId")) {
            queryParams.delete("sessionId");
            history.replace({
                search: queryParams.toString()
            });
        }
    }, [location, history]);

    const previous = useCallback(() => {
        setSessionParams({
            subTypeId: deploymentId,
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
        removeSessionIdFromURL();
    }, [deploymentId, cursors.firstCursor, cursors.lastCursor, removeSessionIdFromURL]);

    const next = useCallback(() => {
        setSessionParams({
            subTypeId: deploymentId,
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
        removeSessionIdFromURL();
    }, [deploymentId, cursors.firstCursor, cursors.lastCursor, removeSessionIdFromURL]);

    const handleClick = useCallback((event, sessionId) => {
        event.preventDefault();
        history.push("?sessionId=" + sessionId);
    }, [history]);

    const renderToolbar = useMemo(() => {
        return (
            <Toolbar
                heading={translate("OTM_SESSIONS")}
                buttons={[
                    {
                        name: "CloudDownload",
                        title: translate("OTM_DOWNLOAD"),
                        onClick: () => setShowDownloadLogsModal(true),
                        disabled: sessionData.length === 0
                    },
                    {
                        name: "ChevronLeft",
                        title: translate("OTM_PREVIOUS"),
                        onClick: previous,
                        disabled: currentPage === 1
                    },
                    {
                        name: "ChevronRight",
                        title: translate("OTM_NEXT"),
                        onClick: next,
                        disabled: !hasNextPage
                    }
                ]}
            />
        );
    }, [translate, previous, currentPage, next, hasNextPage, sessionData]);

    const renderList = useMemo(() => {
        if (!permission.read) {
            return (
                <li className="list-group-item small">
                    {permission.read === false ? translate("OTM_NO_PERMISSION_LOGS") : translate("OTM_LOADING")}
                </li>
            )
        } else {
            if (sessionData.length === 0) {
                return (
                    <li className="list-group-item small">
                        {translate("OTM_NO_SESSIONS")}
                    </li>
                );
            } else {
                return sessionData.map((session) => (
                    <button
                        className={`list-group-item list-group-item-action small ${sessionId === session.sessionId ? "active" : ""}`}
                        key={session._id}
                        onClick={(event) => handleClick(event, session.sessionId)}
                    >
                        <div>{Utils.getFormattedDateTime(session.createdAt)}</div>
                        <div>
                            {session.username || translate("OTM_NO_USER")}
                        </div>
                        <div>
                            {session.noOfErrors > 0 && (
                                <span className="badge bg-danger rounded-pill me-2">
                                    {session.noOfErrors} {translate("OTM_ERRORS")}
                                </span>
                            )}
                            {session.noOfWarnings > 0 && (
                                <span className="badge bg-warning rounded-pill">
                                    {session.noOfWarnings} {translate("OTM_WARNINGS")}
                                </span>
                            )}
                        </div>
                    </button>
                ));
            }
        }
    }, [permission, sessionData, sessionId, translate, handleClick]);

    if (!permission.read) {
        return (<></>)
    }

    return (
        <div className="card">
            <div className="card-header border-bottom">
                {renderToolbar}
            </div>
            <div className="card-body">
                <ul className="list-group">
                    {renderList}
                </ul>
            </div>
            <LogsDownload
                show={showDownloadLogsModal}
                onCancel={() => setShowDownloadLogsModal(false)}
                deploymentId={deploymentId}
            />
        </div>
    );
};

export default SessionList;
